// src/hooks/useKunden.js
// --- KUNDEN HOOK --------------------------------------------------------------
// Lädt Kunden mit Suche und Pagination. Online-First (Stammdaten brauchen
// keinen Offline-Sync; werden vor Wartungs-Einsätzen einmal geladen).

import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../lib/supabase';
import { offeneZeilen } from '../lib/warteschlange';
import { useDebounced } from './useDebounced';

const DEFAULT_PAGE_SIZE = 50;

export function useKunden({ search: rohSearch = '', pageSize = DEFAULT_PAGE_SIZE } = {}) {
  // Erst suchen, wenn der Nutzer kurz Pause macht - sonst eine Server-Anfrage
  // pro Tastendruck.
  const search = useDebounced(rohSearch, 350);
  const [kunden, setKunden] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  // Nur beim ALLERERSTEN Laden die Liste durch "laedt ..." ersetzen. Beim
  // Tippen bleibt das alte Ergebnis stehen, bis das neue da ist - sonst
  // blinkt die Liste auf dem Handy bei jedem Buchstaben weg und es fuehlt
  // sich an, als lade die Seite neu.
  const ersteLadung = useRef(true);
  const [error, setError] = useState(null);

  // Bei jeder Änderung von search/pageSize zur Seite 0 zurück
  useEffect(() => { setPage(0); }, [search, pageSize]);

  const load = useCallback(async (signal) => {
    if (ersteLadung.current) setLoading(true);
    setError(null);
    try {
      const queryPromise = db.kunden.list({
        search,
        limit: pageSize,
        offset: page * pageSize,
      });
      const timeoutPromise = new Promise((_, rej) => setTimeout(() => rej(new Error('Timeout: Kunden laden')), 15000));
      const { data, count: total, error: err } = await Promise.race([queryPromise, timeoutPromise]);
      if (signal?.aborted) return;
      if (err) throw err;
      setKunden(data || []);
      setCount(total || 0);
    } catch (e) {
      if (signal?.aborted) return;
      setError(e.message || 'Fehler beim Laden der Kunden');
      setKunden([]);
    } finally {
      if (!signal?.aborted) { setLoading(false); ersteLadung.current = false; }
    }
  }, [search, page, pageSize]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const hasNext = page < totalPages - 1;
  const hasPrev = page > 0;

  return {
    kunden,
    count,
    loading,
    error,
    page,
    setPage,
    totalPages,
    hasNext,
    hasPrev,
    nextPage: () => hasNext && setPage(p => p + 1),
    prevPage: () => hasPrev && setPage(p => p - 1),
    reload: load,
  };
}

// Einzelnen Kunden mit Stationen laden
export function useKunde(kundeId) {
  const [kunde, setKunde] = useState(null);
  const [stationen, setStationen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // "Laedt ..." nur beim ERSTEN Laden - danach im Hintergrund (Regel aus CLAUDE.md).
  const ersteLadungKunde = useRef(true);
  const load = useCallback(async () => {
    if (!kundeId) {
      setKunde(null);
      setStationen([]);
      setLoading(false);
      return;
    }
    if (ersteLadungKunde.current) setLoading(true);
    setError(null);
    try {
      const [{ data: k, error: ke }, { data: s, error: se }] = await Promise.all([
        db.kunden.get(kundeId),
        db.stationen.listByKunde(kundeId),
      ]);
      if (ke) throw ke;
      if (se) throw se;
      // Noch nicht gesendete Aenderung aus dem Protokoll darueberlegen.
      const offen = offeneZeilen('kunde').find(p => p.id === kundeId);
      setKunde(offen && k ? { ...k, ...offen } : k);
      setStationen(s || []);
    } catch (e) {
      setError(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [kundeId]);

  useEffect(() => { load(); }, [load]);

  return { kunde, stationen, loading, error, reload: load };
}
